export interface ProjectListItem { id: string; title: string; role: 'owner' | 'editor' | 'viewer'; updatedAt: number; coverUpdatedAt?: number; }
export interface ProjectDetail { id: string; title: string; role: ProjectListItem['role']; docVersion: number; doc: string; coverUpdatedAt?: number; }
export interface Member { sub: string; role: ProjectListItem['role']; status: 'active' | 'invited'; invitedEmail?: string; }

export class ConflictError extends Error {
  constructor(public docVersion: number, public serverDoc: string) { super('version conflict'); }
}

/** 409 from a project whose doc is owned by the live-sync (yjs) path: REST
 *  writes are permanently rejected — the client must reload into live sync,
 *  not merge-retry. */
export class SyncModeError extends Error {
  constructor() { super('project uses live sync'); }
}

/** Typed client for the proxy /projects API. `getToken` returns the current
 *  Cognito access token (or '' in local dev — the proxy treats it as the dev user). */
export class ProjectsApi {
  constructor(private base: string, private getToken: () => Promise<string>) {}

  private async req(method: string, path: string, body?: unknown): Promise<Response> {
    const token = await this.getToken();
    const r = await fetch(`${this.base}${path}`, {
      method,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(body ? { 'Content-Type': 'application/json' } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    // Fail loudly on errors so an error body never gets parsed as data (a 500
    // returning {error} would otherwise make list() non-array → picker crash).
    // 409 is the one expected non-ok status; saveDoc handles it.
    if (!r.ok && r.status !== 409) throw new Error(`${method} ${path} → ${r.status}`);
    return r;
  }

  async list(): Promise<ProjectListItem[]> { return (await this.req('GET', '/projects')).json(); }
  async create(title: string): Promise<{ id: string; title: string; role: 'owner'; docVersion: number }> {
    return (await this.req('POST', '/projects', { title })).json();
  }
  async get(id: string): Promise<ProjectDetail> { return (await this.req('GET', `/projects/${id}`)).json(); }
  async saveDoc(id: string, doc: string, baseVersion: number): Promise<{ docVersion: number }> {
    const r = await this.req('PUT', `/projects/${id}/doc`, { doc, baseVersion });
    if (r.status === 409) {
      const b = await r.json();
      if (b.syncMode === 'yjs') throw new SyncModeError();
      throw new ConflictError(b.docVersion, b.serverDoc);
    }
    return r.json();
  }
  async rename(id: string, title: string): Promise<void> { await this.req('PATCH', `/projects/${id}`, { title }); }
  async remove(id: string): Promise<void> { await this.req('DELETE', `/projects/${id}`); }
  async members(id: string): Promise<Member[]> { return (await this.req('GET', `/projects/${id}/members`)).json(); }
  async share(id: string, email: string, role: 'editor' | 'viewer'): Promise<{ sub: string; status: string; created: boolean }> {
    return (await this.req('POST', `/projects/${id}/members`, { email, role })).json();
  }
  async revoke(id: string, sub: string): Promise<void> { await this.req('DELETE', `/projects/${id}/members/${sub}`); }
  async getCover(id: string): Promise<string | null> {
    const token = await this.getToken();
    const r = await fetch(`${this.base}/projects/${id}/cover`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (r.status === 404) return null;
    if (!r.ok) throw new Error(`GET cover ${id} → ${r.status}`);
    return (await r.json()).dataUrl as string;
  }
  async putCover(id: string, dataUrl: string): Promise<void> {
    await this.req('PUT', `/projects/${id}/cover`, { dataUrl });
  }
  /** Upload an assembled static site (publish/assemblePage output) and get back
   *  its public URL. */
  async publish(id: string, req: PublishUpload): Promise<PublishedSite> {
    return (await this.req('POST', `/projects/${id}/publish`, req)).json();
  }
}

/** Serialized assemblePage output uploaded to the publish endpoint. */
export interface PublishUpload {
  slug?: string;
  files: Record<string, string>;
  assets?: Record<string, string>;
}
export interface PublishedSite {
  url: string;
  slug: string;
  publishedAt: number;
}
